var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var RuleMapperV2Config_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import './sortColumn'; // force import
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
import { ruleMapperV2ConfigSchemaValidation } from '../validations.generated';
const MAX_COLUMNS = 100;
export const ruleMapperV2ConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:RuleMapperV2Config',
    type: 'object',
    properties: {
        dataOutputColumns: {
            type: 'array',
            items: { type: 'string', minLength: 1 },
            minItems: 1,
            maxItems: MAX_COLUMNS,
            uniqueItems: true
        },
        dataDateColumn: { type: 'string', minLength: 1 },
        dataLeavesOnly: { type: 'boolean' },
        hierarchiesSourceColumn: { type: 'string', minLength: 1 },
        hierarchiesIDColumn: { type: 'string', minLength: 1 },
        hierarchiesParentColumn: { type: 'string', minLength: 1 },
        hierarchiesParentByValue: { type: 'boolean' },
        hierarchiesValueColumn: { type: 'string', minLength: 1 },
        hierarchiesStartColumn: {
            type: 'string',
            minLength: 1,
            allOf: [
                { not: { const: { $data: '1/hierarchiesSourceColumn' } } },
                { not: { const: { $data: '1/hierarchiesIDColumn' } } }
            ]
        },
        hierarchiesEndColumn: {
            type: 'string',
            minLength: 1,
            allOf: [
                { not: { const: { $data: '1/hierarchiesSourceColumn' } } },
                { not: { const: { $data: '1/hierarchiesIDColumn' } } }
            ]
        },
        hierarchiesAttributeColumns: {
            type: 'array',
            items: { type: 'string', minLength: 1 },
            maxItems: MAX_COLUMNS,
            uniqueItems: true
        },
        rulesOutputColumns: {
            type: 'array',
            items: { type: 'string', minLength: 1 },
            minItems: 1,
            maxItems: MAX_COLUMNS,
            uniqueItems: true
        },
        rulesDefColumn: { type: 'string', minLength: 1 },
        rulesValueColumn: { type: 'string', minLength: 1 },
        rulesPrecedenceColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:SortColumn' },
            uniqueItemProperties: ['name'],
            maxItems: 10
        },
        rulesStartColumn: {
            type: 'string',
            minLength: 1,
            not: { const: { $data: '1/rulesDefColumn' } }
        },
        rulesEndColumn: {
            type: 'string',
            minLength: 1,
            not: { const: { $data: '1/rulesDefColumn' } }
        },
        rulesDecodeMethod: { type: 'string', enum: ['text', 'json'] },
        rulesMultiValues: { type: 'boolean' },
        rulesSplitColumn: { type: 'string', minLength: 1 },
        outputUnmapped: { type: 'boolean' }
    },
    required: [
        'dataOutputColumns',
        'hierarchiesSourceColumn',
        'hierarchiesIDColumn',
        'hierarchiesParentColumn',
        'hierarchiesParentByValue',
        'hierarchiesValueColumn',
        'rulesOutputColumns',
        'rulesDefColumn',
        'rulesValueColumn',
        'rulesPrecedenceColumns'
    ],
    allOf: [
        {
            if: { required: ['rulesStartColumn'] },
            then: { required: ['rulesEndColumn', 'dataDateColumn'] }
        },
        {
            if: { required: ['rulesEndColumn'] },
            then: { required: ['rulesStartColumn', 'dataDateColumn'] }
        },
        {
            if: { required: ['hierarchiesStartColumn'] },
            then: { required: ['hierarchiesEndColumn', 'dataDateColumn'] }
        },
        {
            if: { required: ['hierarchiesEndColumn'] },
            then: { required: ['hierarchiesStartColumn', 'dataDateColumn'] }
        },
        {
            if: { properties: { rulesMultiValues: { const: true } } },
            then: {
                properties: {
                    rulesStartColumn: {
                        type: 'string',
                        not: { const: { $data: '1/rulesValueColumn' } }
                    },
                    rulesEndColumn: {
                        type: 'string',
                        not: { const: { $data: '1/rulesValueColumn' } }
                    }
                }
            }
        }
    ]
};
let RuleMapperV2Config = RuleMapperV2Config_1 = class RuleMapperV2Config extends ToolConfig {
    constructor(dataOutputColumns, dataDateColumn, dataLeavesOnly, hierarchiesSourceColumn, hierarchiesIDColumn, hierarchiesParentColumn, hierarchiesParentByValue, hierarchiesValueColumn, hierarchiesStartColumn, hierarchiesEndColumn, hierarchiesAttributeColumns, rulesOutputColumns, rulesDefColumn, rulesValueColumn, rulesPrecedenceColumns, rulesStartColumn, rulesEndColumn, rulesDecodeMethod, rulesMultiValues, rulesSplitColumn, outputUnmapped, customFormats) {
        super(ToolType.RULE_MAPPERV2, customFormats);
        this.dataOutputColumns = dataOutputColumns;
        if (dataDateColumn !== undefined)
            this.dataDateColumn = dataDateColumn;
        this.dataLeavesOnly = dataLeavesOnly ?? false;
        this.hierarchiesSourceColumn = hierarchiesSourceColumn;
        this.hierarchiesIDColumn = hierarchiesIDColumn;
        this.hierarchiesParentColumn = hierarchiesParentColumn;
        this.hierarchiesParentByValue = hierarchiesParentByValue;
        this.hierarchiesValueColumn = hierarchiesValueColumn;
        if (hierarchiesStartColumn !== undefined)
            this.hierarchiesStartColumn = hierarchiesStartColumn;
        if (hierarchiesEndColumn !== undefined)
            this.hierarchiesEndColumn = hierarchiesEndColumn;
        if (hierarchiesAttributeColumns !== undefined)
            this.hierarchiesAttributeColumns = hierarchiesAttributeColumns;
        this.rulesOutputColumns = rulesOutputColumns;
        this.rulesDefColumn = rulesDefColumn;
        this.rulesValueColumn = rulesValueColumn;
        this.rulesPrecedenceColumns = rulesPrecedenceColumns;
        if (rulesStartColumn !== undefined)
            this.rulesStartColumn = rulesStartColumn;
        if (rulesEndColumn !== undefined)
            this.rulesEndColumn = rulesEndColumn;
        this.rulesDecodeMethod = rulesDecodeMethod ?? 'text';
        this.rulesMultiValues = rulesMultiValues ?? false;
        if (rulesSplitColumn !== undefined)
            this.rulesSplitColumn = rulesSplitColumn;
        if (outputUnmapped !== undefined)
            this.outputUnmapped = outputUnmapped;
        JSON.validate(this, RuleMapperV2Config_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get dataIn() {
        return RuleMapperV2Config_1.dataIn;
    }
    // eslint-disable-next-line class-methods-use-this
    get hierarchiesIn() {
        return RuleMapperV2Config_1.hierarchiesIn;
    }
    // eslint-disable-next-line class-methods-use-this
    get rulesIn() {
        return RuleMapperV2Config_1.rulesIn;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return RuleMapperV2Config_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return RuleMapperV2Config_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return RuleMapperV2Config_1.outs;
    }
    static get dataIn() {
        return RuleMapperV2Config_1.ins[0];
    }
    static get hierarchiesIn() {
        return RuleMapperV2Config_1.ins[1];
    }
    static get rulesIn() {
        return RuleMapperV2Config_1.ins[2];
    }
    static get out() {
        return RuleMapperV2Config_1.outs[0];
    }
    static get ins() {
        return ['data', 'hierarchies', 'rules'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo('Rule-Based Assignment', [ToolCategories.SPM], this.ins, this.outs, ['territory management'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const outputs = [
            { port: this.dataIn, columns: this.dataOutputColumns },
            {
                port: this.rulesIn,
                columns: [...this.rulesOutputColumns, this.rulesValueColumn]
            }
        ];
        let rulesValueColumn;
        for (const { port, columns } of outputs) {
            const source = ins.schemas.at(port);
            for (const name of columns) {
                const column = source.find((c) => c.name === name);
                exp.copy(port, column, true, true);
                if (port === this.rulesIn && name === this.rulesValueColumn) {
                    rulesValueColumn = column;
                }
            }
        }
        if (this.rulesMultiValues && this.rulesSplitColumn !== undefined) {
            exp.makeNew(new Column(this.rulesSplitColumn, 'string'), [rulesValueColumn], true);
        }
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const sources = new Map();
        for (const port of this.ins) {
            const source = scope.schemas.at(port);
            if (!source) {
                return ToolValidationState.BAD_METADATA;
            }
            sources.set(port, source);
        }
        const outputs = [];
        const dataInputs = new Map();
        for (const column of this.dataOutputColumns) {
            dataInputs.set(column, []);
        }
        if (this.hierarchiesStartColumn !== undefined || this.rulesStartColumn !== undefined) {
            dataInputs.set(this.dataDateColumn, ['date']);
        }
        if (!isSourceValid(sources.get(this.dataIn), dataInputs, [])) {
            return ToolValidationState.BAD_METADATA;
        }
        outputs.push(...this.dataOutputColumns);
        const hierarchiesSource = sources.get(this.hierarchiesIn);
        const hierarchiesInputs = new Map([
            [this.hierarchiesParentColumn, []],
            [this.hierarchiesValueColumn, []]
        ]);
        hierarchiesInputs.set(this.hierarchiesSourceColumn, ['string']);
        hierarchiesInputs.set(this.hierarchiesIDColumn, ['string']);
        if (this.hierarchiesStartColumn !== undefined && this.hierarchiesEndColumn !== undefined) {
            hierarchiesInputs.set(this.hierarchiesStartColumn, ['date']);
            hierarchiesInputs.set(this.hierarchiesEndColumn, ['date']);
        }
        if (!isSourceValid(hierarchiesSource, hierarchiesInputs, [])) {
            return ToolValidationState.BAD_METADATA;
        }
        const hierarchiesTypes = hierarchiesSource.reduce((types, column) => {
            if (!types.has(column.name)) {
                types.set(column.name, column.type);
            }
            return types;
        }, new Map());
        const parentType = hierarchiesTypes.get(this.hierarchiesParentColumn);
        const parentTargetType = hierarchiesTypes.get(this.hierarchiesParentByValue ? this.hierarchiesValueColumn : this.hierarchiesIDColumn);
        if (parentType !== parentTargetType) {
            return ToolValidationState.BAD_METADATA;
        }
        const rulesInputs = new Map();
        for (const column of this.rulesOutputColumns) {
            rulesInputs.set(column, []);
        }
        for (const column of this.rulesPrecedenceColumns) {
            rulesInputs.set(iConfigHelper.getParameters().getStrVal(column.name), []);
        }
        rulesInputs.set(this.rulesValueColumn, this.rulesMultiValues === true ? ['string'] : []);
        rulesInputs.set(this.rulesDefColumn, ['string']);
        if (this.rulesStartColumn !== undefined && this.rulesEndColumn !== undefined) {
            rulesInputs.set(this.rulesStartColumn, ['date']);
            rulesInputs.set(this.rulesEndColumn, ['date']);
        }
        if (!isSourceValid(sources.get(this.rulesIn), rulesInputs, [])) {
            return ToolValidationState.BAD_METADATA;
        }
        outputs.push(...this.rulesOutputColumns, this.rulesValueColumn);
        if (this.rulesMultiValues === true && this.rulesSplitColumn !== undefined) {
            outputs.push(this.rulesSplitColumn);
        }
        if (new Set(outputs).size !== outputs.length) {
            return ToolValidationState.BAD_METADATA;
        }
        return ToolValidationState.VALID;
    }
};
RuleMapperV2Config.MAX_COLUMNS = MAX_COLUMNS;
RuleMapperV2Config = RuleMapperV2Config_1 = __decorate([
    JsonSchemaV2(ruleMapperV2ConfigSchema, ruleMapperV2ConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.RULE_MAPPERV2)
], RuleMapperV2Config);
export { RuleMapperV2Config };
//# sourceMappingURL=ruleMapperV2Config.js.map